// src/pages/CompanySetup.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Upload, Typography, Steps, Space, message } from 'antd';
import { 
  UploadOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  BankOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useGetIdentity } from '@refinedev/core';
import { auth, db } from '../utils/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getCurrentUserToken } from '../providers/authProvider';

const { Title, Text } = Typography;
const { Step } = Steps;
const { TextArea } = Input;

const CompanySetup: React.FC = () => {
  const navigate = useNavigate();
  const { data: user, refetch } = useGetIdentity<any>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [fileList, setFileList] = useState<any[]>([]);

  // Refresh user data periodically to catch Stripe webhook updates
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 2000); // Check every 2 seconds

    return () => clearInterval(interval);
  }, [refetch]);

  const handleLogoUpload = async (file: any) => {
    if (!auth.currentUser) return false;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    // Validate file size (max 5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }

    setUploading(true);
    try {
      // Create a reference to the logo in Firebase Storage
      const storage = getStorage();
      const timestamp = Date.now();
      const fileName = `logos/${auth.currentUser.uid}/company-logo-${timestamp}`;
      const storageRef = ref(storage, fileName);

      // Upload the file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setLogoUrl(downloadURL);
      setFileList([{
        uid: '-1',
        name: file.name,
        status: 'done',
        url: downloadURL,
      }]);
      
      message.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      message.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleRemoveLogo = () => {
    setLogoUrl('');
    setFileList([]);
  };

  const handleFinish = async (values: any) => {
    setLoading(true);
    try {
      if (!auth.currentUser) {
        throw new Error('No authenticated user');
      }

      console.log('Saving company info for user:', auth.currentUser.uid);
      console.log('Values:', values);
      console.log('Logo URL:', logoUrl);

      // Update user document with company info
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        company: values.company,
        companyRole: values.companyRole,
        companyDescription: values.companyDescription || '',
        companyLogo: logoUrl || null,
        setupCompleted: true,
        updatedAt: new Date()
      });
      
      try {
        console.log('updating ghl contact with company');
        console.debug(JSON.stringify({
            uid: auth.currentUser?.uid,
            email: user?.email,
            name: user?.name,
            company: values.company
          }));

        const token = await getCurrentUserToken();
        if (!token) {
        message.error('No authentication token');
        return;
        }

        // Note: Use buildApiUrl or authenticatedFetch from authProvider if impersonation support needed, but since new user, direct fetch is fine
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/update-company-info`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: auth.currentUser?.uid,
            email: user?.email,
            name: user?.name,
            company: values.company
          }),
        });
        
        if (!response.ok) {
          console.error('Failed to create GHL contact:', await response.text());
        }
      } catch (ghlError) {
        console.error('Failed to create GHL contact:', ghlError);
      }        


      message.success('Company setup completed!');
      
      // Navigate to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
      
    } catch (error) {
      console.error('Error updating company info:', error);
      message.error(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <Card 
        style={{
          width: '100%',
          maxWidth: '600px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        {/* Steps indicator */}
        <Steps current={2} style={{ marginBottom: '32px' }}>
          <Step title={<span style={{ fontSize: '14px' }}>Account</span>} icon={<CheckCircleOutlined />} />
          <Step title={<span style={{ fontSize: '14px' }}>Plan</span>} icon={<CheckCircleOutlined />} />
          <Step title={<span style={{ fontSize: '14px' }}>Company</span>} icon={<BankOutlined />} />
        </Steps>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Set Up Your Company
          </Title>
          <p style={{ color: '#666', margin: 0 }}>
            Add your business details to personalize your videos
          </p>
        </div>

        {/* Show plan status */}
        {user && (
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '24px',
            padding: '12px',
            backgroundColor: user.plan === 'free' ? '#f0f0f0' : '#e6f7ff',
            borderRadius: '8px'
          }}>
            <Text>
              {user.plan === 'free' ? (
                <>Welcome to Spectreviews! Complete your setup below.</>
              ) : (
                <>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                  Your {user.plan} plan is active! Let's set up your company.
                </>
              )}
            </Text>
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Company Name"
            name="company"
            rules={[{ required: true, message: 'Please enter your company name!' }]}
          >
            <Input 
              prefix={<BankOutlined />}
              size="large" 
              placeholder="e.g., ABC Home Inspections"
            />
          </Form.Item>

          <Form.Item
            label="Your Role"
            name="companyRole"
            rules={[{ required: true, message: 'Please enter your role!' }]}
          >
            <Input 
              prefix={<UserOutlined />}
              size="large" 
              placeholder="e.g., Owner, Broker, Agent"
            />
          </Form.Item>

          <Form.Item
            label="Company Description (Optional)"
            name="companyDescription"
            help="This helps personalize your video content"
          >
            <TextArea 
              rows={3}
              placeholder="Brief description of your company and services..."
              maxLength={200}
              showCount
            />
          </Form.Item>
          <br/>
          <Form.Item label="Company Logo (Optional)">
            <Space direction="vertical" style={{ width: '100%' }}>
              {logoUrl && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ 
                    width: '100%',
                    maxWidth: 300,
                    height: 150,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0',
                    margin: '0 auto'
                  }}>
                    <img 
                      src={logoUrl} 
                      alt="Company Logo"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                </div>
              )}
              
              <Upload
                name="logo"
                listType="picture"
                fileList={fileList}
                beforeUpload={handleLogoUpload}
                onRemove={handleRemoveLogo}
                maxCount={1}
                showUploadList={false}
              >
                <Button 
                  icon={uploading ? <LoadingOutlined /> : <UploadOutlined />} 
                  loading={uploading}
                  size="large"
                  style={{ width: '100%' }}
                >
                  {logoUrl ? 'Change Logo' : 'Upload Company Logo'}
                </Button>
              </Upload>
              
              <div style={{ color: '#888', fontSize: '12px', textAlign: 'center' }}>
                Your logo will appear in generated videos
                <br />
                Recommended: Square image, at least 300x300px, max 5MB
              </div>
            </Space>
          </Form.Item>
          
          <Form.Item style={{ marginTop: '32px', marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button 
                type="text" 
                onClick={handleSkip}
                style={{ color: '#999' }}
              >
                Skip for now
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                size="large"
                icon={loading ? <LoadingOutlined /> : undefined}
              >
                {loading ? 'Saving...' : 'Complete Setup'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CompanySetup;